<template lang="pug">
    include ../../icon/arrow

    component(
        :is="hidden ? 'div' : 'th'",
        :class="{ sort, sorted: active }",
        :aria-hidden="hidden",
        v-on="_on")
        span(v-if="show && sort && numeric", class="_tha")
            template(v-if="dir > 0")
                +icon-arrow-sort_up
            template(v-else)
                +icon-arrow-sort_down
            | &nbsp;
        span(v-if="show", class="_thc")
            slot
        span(v-if="show && sort && !numeric", class="_tha")
            | &nbsp;
            template(v-if="dir > 0")
                +icon-arrow-sort_up
            template(v-else)
                +icon-arrow-sort_down
</template>

<script>
    const indexOf = Array.prototype.indexOf;


    /**
     * Definition of a column within an [ib-table-basic](#ibtablebasic) component. Specify the
     * title in the default slot; do not use `v-text`, `v-markdown`, or `v-html`.
     */
    export default {
        name: 'ib-table-col',

        props: {
            /**
             * A convenience property; when a table emits an `@sort` event, it includes
             * a reference to this `ib-table-col` component. Including a name will help
             * determine which column that is.
             */
            name: String,
            /**
             * The minimum width for this column, in any CSS units. If the table is wider than the
             * sum of all column widths, columns will expand proportionally to their width to fill
             * the space, *unless* the column is forced mode, or there are other columns with
             * percentage unit width.
             */
            width: String,
            /**
             * Prevent this column from expanding if the table is wider than the sum of all column
             * widths.
             */
            force: Boolean,
            /**
             * If true, allow this column to be dropped if the table becomes too narrow, to avoid
             * displaying a scroll bar. If numeric, force the priority with which this column will
             * be dropped, where lower numbers (starting from 1) are dropped first. Otherwise,
             * narrower columns will be dropped first; this reduces the appearance of popping.
             */
            drop: [Boolean,Number],
            /**
             * Member of data object to sort by, if any, or function to retrieve a sortable value.
             * You are generally encouraged to specify this as a member name; if you provide it as
             * a function, **DO NOT** inline it into your template as that will cause the function
             * to be rebuilt and the table's reactivity to be triggered every time the parent
             * renders. Also, **DO NOT** perform anything compute intensive such as indexOf or a
             * map or loop. You can specify it without an argument (i.e. the Boolean value of
             * `true`) and the column will have a clickable sort arrow, and the table will emit
             * a `sort` event, but no sorting will be performed internally. You can use this if
             * you intend the backend to perform sorting.
             */
            sort: [Boolean,String,Number,Function],
            /**
             * If true, start the table with this column sorted.
             */
            sorted: Boolean,
            /**
             * If true, start the table with this column's sort arrow descending.
             */
            descending: Boolean,
            /**
             * Dock this column so that it never goes out of view during horizontal scrolling.
             * Valid values are 'start' (left) and 'end' (right); true means start.
             */
            sticky: [Boolean,String],
            /**
             * If true, display this column with end (right) alignment and monospaced numbers.
             */
            numeric: Boolean,
            /**
             * Set text alignment; this can override the end alignment in `numeric`.
             * Supported values are `start`, `center`, and `end`.
             */
            align: String,
            /**
             * Used internally; allows shadow columns to have aria-hidden="true".
             */
            hidden: Boolean
        },

        data() {
            return {
                dir: this.descending ? -1 : 1,
                active: !!this.sorted,
                show: !!this.$slots.default,
            };
        },

        computed: {
            sortfn() {
                const sort = this.sort;
                return !sort || sort === true ? null : typeof sort === 'function' ? sort : row => row[sort];
            },

            _on() {
                return this.sort ? {
                    click: this.click,
                    mouseenter: this.mouseenter,
                    mouseleave: this.mouseleave,
                    touchstart: this.touchstart
                } : null;
            }
        },

        watch: {
            width: 'sync',
            force: 'sync',
            drop: 'sync',
            show: 'sync'
        },

        mounted() {
            const sticky = this.sticky;
            if (sticky && sticky !== true && sticky != 'start' && sticky != 'end') {
                throw new RangeError('Invalid sticky position: ' + sticky);
            }
            this.sync();
            if (this.sorted) {
                this.$parent.sort = {
                    col: this,
                    dir: this.dir || -1
                };
            }
        },

        beforeUpdate() {
            this.show = !!this.$slots.default;
        },

        beforeDestroy() {
            this.$nextTick(this.sync);
        },

        methods: {
            /** Notify table of a potential layout change */
            sync() {
                this.$parent.sync();
            },

            click() {
                this.sortBy(this.active ? -this.dir : this.dir);
            },

            touchstart(e) {
                if (e.which == 0) {
                    this.mouseenter();
                    document.addEventListener('touchmove', this.touchend, false);
                    document.addEventListener('touchend', this.touchend, false);
                }
            },

            touchend(e) {
                if (e.which == 0) {
                    this.mouseleave();
                    document.removeEventListener('touchmove', this.touchend, false);
                    document.removeEventListener('touchend', this.touchend, false);
                }
            },

            mouseenter() {
                this.$parent.colhover = indexOf.call(this.$el.parentElement.children, this.$el);
            },

            mouseleave() {
                this.$parent.colhover = -1;
            },

            /**
             * Sort the enclosing table by this column, with specified direction.
             *
             * @param {Number} dir
             * @public
             */
            sortBy(dir = 1) {
                const table = this.$parent;
                if (this.sort) {
                    this.dir = dir;
                    if (!this.active) {
                        if (table.sort.col) {
                            table.sort.col.active = false;
                        }
                        this.active = true;
                    }
                    table.sort = {
                        col: this,
                        dir: this.dir
                    };
                }
            }
        }
    }
</script>
